import { AppTable, SortDirectionType } from '../_models/models';

export interface ExchangeSortConfig {
  table: AppTable;
  queryParams: any;
  defaultSorts?: any[];
  refreshCallback: () => void;
  fieldMappings?: Record<string, string>;
}

// Known field mappings for exchange tables
const KNOWN_FIELD_MAPPINGS: Record<string, string> = {
  'mediaType': 'segment.type',
};

export function handleExchangeTableSort(
  table: AppTable,
  config: ExchangeSortConfig
): void {
  try {
    config.table.sortBy = table.sortBy;
    config.table.sortDirection = table.sortDirection;
    config.table.pageIndex = 1;

    const mappedField = applyKnownFieldMappings(table.sortBy, config.fieldMappings);

    const sortDirection: SortDirectionType = table.sortDirection.toUpperCase() === 'ASC' ? 'ASC' : 'DESC';

    const sortArray = buildSortArray(mappedField, sortDirection, config.defaultSorts);

    const queryType = detectQueryType(config.queryParams);
    updateQueryParameters(config.queryParams, sortArray, queryType);

    config.refreshCallback();

  } catch (error) {
    console.error('Exchange table sort error:', error);
    config.refreshCallback();
  }
}

function buildSortArray(
  primaryField: string,
  primaryDirection: SortDirectionType,
  defaultSorts: any[] = []
): any[] {
  const primarySort = createSortObject(primaryField, primaryDirection);

  if (!defaultSorts.length) {
    return [primarySort];
  }

  const filteredDefaults = removeExistingSortForField(defaultSorts, primaryField);

  return [primarySort, ...filteredDefaults];
}

function createSortObject(field: string, direction: SortDirectionType): any {
  return { field, order: direction };
}

function removeExistingSortForField(sorts: any[], fieldToRemove: string): any[] {
  return sorts.filter(sort => {
    // Handle different sort object structures
    const sortField = sort.field || sort.sortingField || sort.fieldName;
    return sortField !== fieldToRemove;
  });
}

function detectQueryType(queryParams: any): 'CreateQueryParams' | 'JapiQuery' | 'ParsedRequestParams' {
  if (queryParams.sorting && queryParams.paging) return 'JapiQuery';
  if (queryParams.sort && Array.isArray(queryParams.sort)) return 'CreateQueryParams';
  if (queryParams.sort && queryParams.page !== undefined) return 'ParsedRequestParams';
  return 'CreateQueryParams';
}

function applyKnownFieldMappings(field: string, customMappings?: Record<string, string>): string {
  if (customMappings?.[field]) return customMappings[field];
  if (KNOWN_FIELD_MAPPINGS[field]) return KNOWN_FIELD_MAPPINGS[field];
  return field;
}

function updateQueryParameters(
  queryParams: any,
  sortArray: any[],
  queryType: string
): void {
  switch (queryType) {
    case 'CreateQueryParams':
      queryParams.sort = sortArray;
      queryParams.page = 1;
      break;

    case 'JapiQuery':
      const primarySort = sortArray[0];
      queryParams.sorting = {
        sortDirection: primarySort.order,
        sortingField: primarySort.field
      };
      queryParams.paging.number = 1;
      break;

    case 'ParsedRequestParams':
      queryParams.sort = sortArray;
      queryParams.page = 1;
      break;

    default:
      queryParams.sort = sortArray;
      if (queryParams.page !== undefined) queryParams.page = 1;
      if (queryParams.paging?.number !== undefined) queryParams.paging.number = 1;
  }
}
